import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../../libs/environment/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    challengeToken: string;
    expiresAtUtc: string;
    otpExpiresInSeconds: number;
    resendAvailableInSeconds: number;
  };
  message: string;
  timestamp: string;
}

export interface Verify2faRequest {
  challengeToken: string;
  code: string;
}

export interface Verify2faResponse {
  success: boolean;
  data: {
    accessToken: string;
    accessTokenExpiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    message: string | null;
    user?: {
      id: number;
      firstName: string;
      lastName: string;
      roleId: number;
      roleName: string;
      permissions: string[];
    };
  };
  message?: string;
  timestamp: string;
}

export interface Resend2faRequest {
  challengeToken: string;
}

export interface Resend2faResponse {
  success: boolean;
  data: {
    challengeToken: string;
    resendAvailableInSeconds: number;
    expiresAtUtc?: string;
    otpExpiresInSeconds?: number;
  };
  message: string;
  timestamp: string;
}

export interface PasswordResetLinkRequest {
  email: string;
}

export interface PasswordResetLinkResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
    accessTokenExpiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    message: string | null;
    user?: {
      id: number;
      firstName: string;
      lastName: string;
      roleId: number;
      roleName: string;
      permissions: string[];
    };
  };
  timestamp: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class LoginApiService {
  private readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiBaseUrl}/Auth/login`, payload);
  }

  verify2fa(payload: Verify2faRequest): Observable<Verify2faResponse> {
    return this.http.post<Verify2faResponse>(`${this.apiBaseUrl}/Auth/verify-2fa`, payload);
  }

  resend2fa(payload: Resend2faRequest): Observable<Resend2faResponse> {
    return this.http.post<Resend2faResponse>(`${this.apiBaseUrl}/Auth/resend-2fa`, payload);
  }

  sendPasswordResetLink(payload: PasswordResetLinkRequest): Observable<PasswordResetLinkResponse> {
    return this.http.post<PasswordResetLinkResponse>(`${this.apiBaseUrl}/Auth/send-password-reset-link`, payload);
  }

  resetPassword(payload: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${this.apiBaseUrl}/Auth/reset-password`, payload);
  }

  refreshToken(payload: RefreshTokenRequest): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(`${this.apiBaseUrl}/Auth/refresh`, payload);
  }

  logout(): Observable<LogoutResponse> {
    return this.http.post<LogoutResponse>(`${this.apiBaseUrl}/Auth/logout`, {});
  }
}
